import React, { useState, useEffect } from 'react';
import {
  ticketAPI, engineerAPI,
  customerAPI, inventoryAPI,
  freelancerAPI
} from '../services/api';

const statusColors = {
  open: { bg:'rgba(0,212,255,0.1)',
    color:'var(--accent)',
    border:'rgba(0,212,255,0.3)' },
  assigned: { bg:'rgba(159,122,234,0.1)',
    color:'var(--purple)',
    border:'rgba(159,122,234,0.3)' },
  in_progress: { bg:'rgba(255,192,64,0.1)',
    color:'var(--yellow)',
    border:'rgba(255,192,64,0.3)' },
  delivered: { bg:'rgba(0,229,160,0.1)',
    color:'var(--green)',
    border:'rgba(0,229,160,0.3)' },
  completed: { bg:'rgba(0,229,160,0.1)',
    color:'var(--green)',
    border:'rgba(0,229,160,0.3)' },
  closed: { bg:'rgba(77,95,122,0.2)',
    color:'var(--text3)',
    border:'rgba(77,95,122,0.3)' },
  cancelled: { bg:'rgba(255,77,109,0.1)',
    color:'var(--red)',
    border:'rgba(255,77,109,0.3)' },
};

const StatusBadge = ({ status }) => {
  const c = statusColors[status]
    || statusColors.open;
  return (
    <span style={{
      background: c.bg, color: c.color,
      border: `1px solid ${c.border}`,
      padding:'3px 10px',
      borderRadius:'20px', fontSize:'11px',
      fontFamily:'DM Mono,monospace',
      textTransform:'capitalize',
    }}>
      {status?.replace('_',' ')}
    </span>
  );
};

const priorityColor = (p) => {
  if (p === 'critical') return 'var(--red)';
  if (p === 'high') return 'var(--orange)';
  if (p === 'normal') return 'var(--accent)';
  return 'var(--text3)';
};

const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] =
    useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [proof, setProof] = useState(null);
  const [visitNotes, setVisitNotes] = useState([]);
  const [filterStatus, setFilterStatus] =
    useState('');
  const [engineers, setEngineers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [assignEng, setAssignEng] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [freelancers, setFreelancers] = useState([]);
  const [assignFreelancerId, setAssignFreelancerId] = useState('');
  const [freelancerInstructions, setFreelancerInstructions] = useState('');
  const [assigningFreelancer, setAssigningFreelancer] = useState(false);
  const [freelancerLink, setFreelancerLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [customerAddresses, setCustomerAddresses] =
    useState([]);
  const [form, setForm] = useState({
    ticket_type:'delivery',
    order_type:'sale',
    priority:'normal',
    customer_id:'',
    customer_address_id:'',
    inventory_id:'',
    contact_person:'',
    contact_phone:'',
    product_description:'',
    serial_number:'',
    quantity:1,
    scheduled_date:'',
    scheduled_slot:'9:00 AM - 12:00 PM',
    assigned_engineer_id:'',
    notes_for_engineer:'',
    rental_start_date:'',
    rental_end_date:'',
    amc_contract_number:'',
  });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [tRes, eRes, cRes, iRes, fRes] =
        await Promise.all([
          ticketAPI.getAll(),
          engineerAPI.getAll(),
          customerAPI.getAll(),
          inventoryAPI.getAll(),
          freelancerAPI.getAll(),
        ]);
      setTickets(tRes.data.data);
      setEngineers(eRes.data.data);
      setCustomers(cRes.data.data);
      setInventory(iRes.data.data);
      setFreelancers(fRes.data);
    } catch (err) {
      console.error('Failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFiltered = async (status) => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      const res = await ticketAPI.getAll(params);
      setTickets(res.data.data);
    } catch (err) {
      console.error('Failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id) => {
  setFreelancerLink('');
  setLinkCopied(false);
  setAssignFreelancerId('');
  setFreelancerInstructions('');
  try {
    const res = await ticketAPI.getOne(id);
    setDetail(res.data.data);
  } catch (err) {
    console.error('Failed:', err);
  }
  try {
    const proofRes = await ticketAPI.getProof(id);
    setProof(proofRes.data.data);
  } catch (err) {
    setProof(null);
  }
  try {
    const notesRes = await ticketAPI.getVisitNotes(id);
    setVisitNotes(notesRes.data.data.notes || []);
  } catch (err) {
    setVisitNotes([]);
  }
};

  const handleCreate = async () => {
    if (!form.ticket_type ||
        !form.customer_id) {
      setError(
        'Ticket type and customer are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await ticketAPI.create(form);
      setForm({
        ticket_type:'delivery',
        order_type:'sale',
        priority:'normal',
        customer_id:'',
        customer_address_id:'',
        inventory_id:'',
        contact_person:'',
        contact_phone:'',
        product_description:'',
        serial_number:'',
        quantity:1,
        scheduled_date:'',
        scheduled_slot:'9:00 AM - 12:00 PM',
        assigned_engineer_id:'',
        notes_for_engineer:'',
        rental_start_date:'',
        rental_end_date:'',
        amc_contract_number:'',
      });
      setShowCreate(false);
      await loadAll();
    } catch (err) {
      setError(
        err.response?.data?.message
        || 'Failed to create ticket');
    } finally {
      setSaving(false);
    }
  };
   const handleCustomerChange = async (customerId) => {
    setForm(prev => ({
      ...prev,
      customer_id: customerId,
      customer_address_id: '',
    }));
    setCustomerAddresses([]);
    if (!customerId) return;
    try {
      const res = await customerAPI.getOne(customerId);
      setCustomerAddresses(
        res.data.data.addresses || []);
    } catch (err) {
      console.error(
        'Failed to load addresses:', err);
    }
  };
  const handleAssign = async (ticketId) => {
    if (!assignEng) {
      alert('Select an engineer first');
      return;
    }
    setAssigning(true);
    try {
      await ticketAPI.assign(ticketId,
        { engineer_id: assignEng });
      await loadDetail(ticketId);
      await loadAll();
      setAssignEng('');
      alert('Engineer assigned!');
    } catch (err) {
      alert('Failed to assign engineer');
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignFreelancer = async (ticketId) => {
    if (!assignFreelancerId) {
      alert('Select a freelancer first');
      return;
    }
    setAssigningFreelancer(true);
    try {
      const res = await ticketAPI.assignFreelancer(ticketId, {
        freelancer_id: assignFreelancerId,
        vendor_instructions: freelancerInstructions || undefined,
      });
      await loadDetail(ticketId);
      await loadAll();
      setFreelancerLink(res.data.link);
      setLinkCopied(false);
    } catch (err) {
      alert(
        err.response?.data?.error
        || 'Failed to assign freelancer');
    } finally {
      setAssigningFreelancer(false);
    }
  };

  const handleRegenerateFreelancerLink = async (ticketId) => {
    if (!detail?.ticket?.assigned_freelancer_id) return;
    if (!window.confirm(
      'Generate a new link for this task? The previous ' +
      'link will stop working immediately.'
    )) return;
    setAssigningFreelancer(true);
    try {
      const res = await ticketAPI.regenerateFreelancerLink(ticketId, {
        freelancer_id: detail.ticket.assigned_freelancer_id,
        vendor_instructions: detail.ticket.vendor_instructions || undefined,
      });
      setFreelancerLink(res.data.link);
      setLinkCopied(false);
    } catch (err) {
      alert('Failed to regenerate link');
    } finally {
      setAssigningFreelancer(false);
    }
  };

  const handleCopyLink = () => {
    if (!freelancerLink) return;
    navigator.clipboard.writeText(freelancerLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2500);
  };

  const handleStatusUpdate = async (
    ticketId, status) => {
    try {
      await ticketAPI.updateStatus(ticketId,
        { status });
      await loadDetail(ticketId);
      await loadAll();
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const handleClose = async (ticketId) => {
    try {
      await ticketAPI.close(ticketId, {});
      await loadDetail(ticketId);
      await loadAll();
      alert('Ticket closed!');
    } catch (err) {
      alert(
        err.response?.data?.message
        || 'Failed to close ticket');
    }
  };

  const inputStyle = {
    width:'100%',
    background:'var(--surface2)',
    border:'1px solid var(--border2)',
    color:'var(--text)',
    padding:'10px 14px',
    borderRadius:'8px',
    fontSize:'13.5px', outline:'none',
  };

  const labelStyle = {
    display:'block', fontSize:'11px',
    fontFamily:'DM Mono,monospace',
    color:'var(--text3)',
    letterSpacing:'1px',
    textTransform:'uppercase',
    marginBottom:'6px',
  };

  const ticketTypes = [
    'delivery','pickup','installation',
    'amc_visit','breakdown','survey'
  ];

  const filteredTickets = filterStatus
    ? tickets.filter(t =>
        t.status === filterStatus)
    : tickets;

  return (
    <div style={{ padding:'24px' }}>
      {/* Header */}
      <div style={{
        display:'flex',
        justifyContent:'space-between',
        alignItems:'center',
        marginBottom:'24px',
      }}>
        <div>
          <h1 style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'22px', fontWeight:'800',
          }}>
            Tickets
          </h1>
          <p style={{
            color:'var(--text3)',
            fontSize:'13px', marginTop:'4px',
          }}>
            {tickets.length} total tickets
          </p>
        </div>
        <button
          onClick={() =>
            setShowCreate(!showCreate)}
          style={{
            background:'var(--accent)',
            color:'var(--bg)', border:'none',
            padding:'10px 20px',
            borderRadius:'8px',
            fontSize:'13px', fontWeight:'600',
            cursor:'pointer',
          }}
        >
          + Create Ticket
        </button>
      </div>

      {/* Create Ticket Form */}
      {showCreate && (
        <div style={{
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'12px',
          padding:'24px', marginBottom:'20px',
        }}>
          <div style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'15px', fontWeight:'700',
            marginBottom:'20px',
          }}>
            Create New Ticket
          </div>
          <div style={{
            display:'grid',
            gridTemplateColumns:'1fr 1fr',
            gap:'16px',
          }}>
            <div>
              <label style={labelStyle}>
                Ticket Type *
              </label>
              <select style={inputStyle}
                value={form.ticket_type}
                onChange={e => setForm({
                  ...form,
                  ticket_type:e.target.value
                })}
              >
                {ticketTypes.map(t => (
                  <option key={t} value={t}>
                    {t.replace('_',' ')
                      .toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>
                Order Type
              </label>
              <select style={inputStyle}
                value={form.order_type}
                onChange={e => setForm({
                  ...form,
                  order_type:e.target.value
                })}
              >
                <option value="sale">Sale</option>
                <option value="rental">
                  Rental
                </option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>
                Priority
              </label>
              <select style={inputStyle}
                value={form.priority}
                onChange={e => setForm({
                  ...form,
                  priority:e.target.value
                })}
              >
                <option value="low">Low</option>
                <option value="normal">
                  Normal
                </option>
                <option value="high">High</option>
                <option value="critical">
                  Critical
                </option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>
                Customer *
              </label>
              <select style={inputStyle}
                value={form.customer_id}
                onChange={e =>
                  handleCustomerChange(
                    e.target.value)}
              >
                <option value="">
                  — Select Customer —
                </option>
                {customers.map(c => (
                  <option key={c.id}
                    value={c.id}>
                    {c.company_name}
                  </option>
                ))}
              </select>
            </div>
            {form.customer_id
              && customerAddresses.length > 0 && (
              <div>
                <label style={labelStyle}>
                  Site Address
                </label>
                <select style={inputStyle}
                  value={form.customer_address_id}
                  onChange={e => setForm({
                    ...form,
                    customer_address_id:
                      e.target.value
                  })}
                >
                  <option value="">
                    — Use Billing Address —
                  </option>
                  {customerAddresses.map(a => (
                    <option key={a.id}
                      value={a.id}>
                      {a.label || 'Address'}
                      {a.city
                        ? ` (${a.city})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label style={labelStyle}>
                Product / Inventory
              </label>
              <select style={inputStyle}
                value={form.inventory_id}
                onChange={e => setForm({
                  ...form,
                  inventory_id:e.target.value
                })}
              >
                <option value="">
                  — Select Product —
                </option>
                {inventory.map(i => (
                  <option key={i.id}
                    value={i.id}>
                    {i.product_name}
                    {i.sku
                      ? ` (${i.sku})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>
                Assign Engineer
              </label>
              <select style={inputStyle}
                value={
                  form.assigned_engineer_id}
                onChange={e => setForm({
                  ...form,
                  assigned_engineer_id:
                    e.target.value
                })}
              >
                <option value="">
                  — Assign Later —
                </option>
                {engineers.map(e => (
                  <option key={e.id}
                    value={e.id}>
                    {e.name} ({e.phone})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>
                Contact Person
              </label>
              <input style={inputStyle}
                placeholder="At customer site"
                value={form.contact_person}
                onChange={e => setForm({
                  ...form,
                  contact_person:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Contact Phone
              </label>
              <input style={inputStyle}
                placeholder="Site contact number"
                value={form.contact_phone}
                onChange={e => setForm({
                  ...form,
                  contact_phone:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Scheduled Date
              </label>
              <input style={inputStyle}
                type="date"
                value={form.scheduled_date}
                onChange={e => setForm({
                  ...form,
                  scheduled_date:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Time Slot
              </label>
              <select style={inputStyle}
                value={form.scheduled_slot}
                onChange={e => setForm({
                  ...form,
                  scheduled_slot:e.target.value
                })}
              >
                <option>
                  9:00 AM - 12:00 PM
                </option>
                <option>
                  12:00 PM - 3:00 PM
                </option>
                <option>
                  3:00 PM - 6:00 PM
                </option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>
                Serial Number
              </label>
              <input style={inputStyle}
                placeholder="Product serial no."
                value={form.serial_number}
                onChange={e => setForm({
                  ...form,
                  serial_number:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Quantity
              </label>
              <input style={inputStyle}
                type="number" min="1"
                value={form.quantity}
                onChange={e => setForm({
                  ...form,
                  quantity:e.target.value
                })}
              />
            </div>
            {form.order_type === 'rental' && (
              <>
                <div>
                  <label style={labelStyle}>
                    Rental Start Date
                  </label>
                  <input style={inputStyle}
                    type="date"
                    value={form.rental_start_date}
                    onChange={e => setForm({
                      ...form,
                      rental_start_date:
                        e.target.value
                    })}
                  />
                </div>
                <div>
                  <label style={labelStyle}>
                    Rental End Date
                  </label>
                  <input style={inputStyle}
                    type="date"
                    value={form.rental_end_date}
                    onChange={e => setForm({
                      ...form,
                      rental_end_date:
                        e.target.value
                    })}
                  />
                </div>
              </>
            )}
            {form.ticket_type ===
              'amc_visit' && (
              <div>
                <label style={labelStyle}>
                  AMC Contract Number
                </label>
                <input style={inputStyle}
                  placeholder="AMC-XXXX"
                  value={
                    form.amc_contract_number}
                  onChange={e => setForm({
                    ...form,
                    amc_contract_number:
                      e.target.value
                  })}
                />
              </div>
            )}
            <div style={{
              gridColumn:'1 / -1',
            }}>
              <label style={labelStyle}>
                Notes For Engineer
              </label>
              <textarea
                style={{
                  ...inputStyle,
                  minHeight:'70px',
                  resize:'vertical',
                }}
                placeholder="Special instructions..."
                value={form.notes_for_engineer}
                onChange={e => setForm({
                  ...form,
                  notes_for_engineer:
                    e.target.value
                })}
              />
            </div>
          </div>

          {error && (
            <div style={{
              background:
                'rgba(255,77,109,0.1)',
              border:
                '1px solid rgba(255,77,109,0.3)',
              color:'var(--red)',
              padding:'10px 14px',
              borderRadius:'8px',
              fontSize:'13px',
              marginTop:'16px',
            }}>
              {error}
            </div>
          )}

          <div style={{
            display:'flex', gap:'10px',
            marginTop:'20px',
          }}>
            <button onClick={handleCreate}
              disabled={saving}
              style={{
                background:'var(--accent)',
                color:'var(--bg)', border:'none',
                padding:'10px 24px',
                borderRadius:'8px',
                fontSize:'13px', fontWeight:'600',
                cursor: saving
                  ? 'not-allowed' : 'pointer',
              }}
            >
              {saving
                ? 'Creating...'
                : 'Create Ticket'}
            </button>
            <button
              onClick={() => {
                setShowCreate(false);
                setError('');
              }}
              style={{
                background:'transparent',
                color:'var(--text2)',
                border:
                  '1px solid var(--border2)',
                padding:'10px 24px',
                borderRadius:'8px',
                fontSize:'13px',
                cursor:'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Status Filter */}
      <div style={{
        display:'flex', gap:'6px',
        marginBottom:'16px', flexWrap:'wrap',
      }}>
        {[
          { key:'', label:'All' },
          { key:'open', label:'Open' },
          { key:'assigned', label:'Assigned' },
          { key:'in_progress',
            label:'In Progress' },
          { key:'delivered',
            label:'Delivered' },
          { key:'closed', label:'Closed' },
          { key:'cancelled',
            label:'Cancelled' },
        ].map(f => (
          <button key={f.key}
            onClick={() => {
              setFilterStatus(f.key);
              loadFiltered(f.key);
            }}
            style={{
              padding:'7px 16px',
              borderRadius:'20px',
              border:
                filterStatus === f.key
                  ? 'none'
                  : '1px solid var(--border2)',
              background:
                filterStatus === f.key
                  ? 'var(--accent)'
                  : 'transparent',
              color:
                filterStatus === f.key
                  ? 'var(--bg)'
                  : 'var(--text2)',
              fontSize:'12px', fontWeight:'600',
              cursor:'pointer',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Main Layout */}
      <div style={{
        display:'grid',
        gridTemplateColumns:
          selected ? '1fr 380px' : '1fr',
        gap:'16px',
      }}>
        {/* Tickets Table */}
        <div style={{
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'12px',
          overflow:'hidden',
        }}>
          {loading ? (
            <div style={{
              padding:'40px',
              textAlign:'center',
              color:'var(--text3)',
              fontFamily:'DM Mono,monospace',
            }}>
              Loading tickets...
            </div>
          ) : filteredTickets.length === 0 ? (
            <div style={{
              padding:'60px',
              textAlign:'center',
              color:'var(--text3)',
            }}>
              <div style={{
                fontSize:'40px',
                marginBottom:'12px',
              }}>◈</div>
              <div style={{
                fontSize:'14px',
                color:'var(--text2)',
                marginBottom:'6px',
              }}>
                No tickets found
              </div>
              <div style={{ fontSize:'12px' }}>
                Click "+ Create Ticket"
                to create your first ticket
              </div>
            </div>
          ) : (
            <table style={{
              width:'100%',
              borderCollapse:'collapse',
            }}>
              <thead>
                <tr>
                  {[
                    'Ticket #','Type',
                    'Customer','Engineer',
                    'Priority','Status',
                    'Scheduled','Actions'
                  ].map(h => (
                    <th key={h} style={{
                      padding:'10px 16px',
                      textAlign:'left',
                      fontSize:'10px',
                      fontFamily:
                        'DM Mono,monospace',
                      color:'var(--text3)',
                      letterSpacing:'1.5px',
                      textTransform:'uppercase',
                      borderBottom:
                        '1px solid var(--border)',
                      background:
                        'var(--surface2)',
                      whiteSpace:'nowrap',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredTickets.map(t => (
                  <tr key={t.id}
                    style={{
                      borderBottom:
                        '1px solid var(--border)',
                      cursor:'pointer',
                      background:
                        selected?.id === t.id
                          ? 'rgba(0,212,255,0.05)'
                          : 'transparent',
                    }}
                    onClick={() => {
                      setSelected(t);
                      loadDetail(t.id);
                    }}
                  >
                    <td style={{
                      padding:'12px 16px',
                      fontFamily:
                        'DM Mono,monospace',
                      color:'var(--accent)',
                      fontWeight:'600',
                      fontSize:'13px',
                    }}>
                      {t.ticket_number}
                    </td>
                    <td style={{
                      padding:'12px 16px',
                      fontSize:'12px',
                      color:'var(--text2)',
                      textTransform:'capitalize',
                    }}>
                      {t.ticket_type
                        ?.replace('_',' ')}
                    </td>
                    <td style={{
                      padding:'12px 16px',
                      fontSize:'13px',
                      fontWeight:'500',
                    }}>
                      {t.customer_name || '—'}
                      {t.customer_city && (
                        <div style={{
                          fontSize:'11px',
                          color:'var(--text3)',
                          marginTop:'2px',
                        }}>
                          {t.customer_city}
                        </div>
                      )}
                    </td>
                    <td style={{
                      padding:'12px 16px',
                      fontSize:'13px',
                      color:'var(--text2)',
                    }}>
                      {t.engineer_name || (
                        <span style={{
                          color:'var(--red)',
                          fontSize:'11px',
                        }}>
                          Unassigned
                        </span>
                      )}
                    </td>
                    <td style={{
                      padding:'12px 16px',
                    }}>
                      <span style={{
                        color: priorityColor(
                          t.priority),
                        fontFamily:
                          'DM Mono,monospace',
                        fontSize:'11px',
                        fontWeight:'600',
                        textTransform:
                          'uppercase',
                      }}>
                        {t.priority}
                      </span>
                    </td>
                    <td style={{
                      padding:'12px 16px',
                    }}>
                      <StatusBadge
                        status={t.status}
                      />
                    </td>
                    <td style={{
                      padding:'12px 16px',
                      fontSize:'12px',
                      fontFamily:
                        'DM Mono,monospace',
                      color:'var(--text3)',
                    }}>
                      {t.scheduled_date
                        ? new Date(
                            t.scheduled_date
                          ).toLocaleDateString(
                            'en-IN')
                        : '—'}
                    </td>
                    <td style={{
                      padding:'12px 16px',
                    }}>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setSelected(t);
                          loadDetail(t.id);
                        }}
                        style={{
                          background:
                            'rgba(0,212,255,0.1)',
                          color:'var(--accent)',
                          border:
                            '1px solid rgba(0,212,255,0.2)',
                          padding:'4px 12px',
                          borderRadius:'6px',
                          fontSize:'12px',
                          cursor:'pointer',
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Ticket Detail Panel */}
        {selected && detail && (
          <div style={{
            background:'var(--surface)',
            border:'1px solid var(--border)',
            borderRadius:'12px',
            overflow:'hidden',
            height:'fit-content',
            maxHeight:'85vh',
            overflowY:'auto',
          }}>
            <div style={{
              padding:'16px 20px',
              borderBottom:
                '1px solid var(--border)',
              display:'flex',
              justifyContent:'space-between',
              alignItems:'center',
              position:'sticky', top:0,
              background:'var(--surface)',
              zIndex:1,
            }}>
              <div>
                <div style={{
                  fontFamily:'Syne,sans-serif',
                  fontSize:'14px',
                  fontWeight:'700',
                }}>
                  {detail.ticket.ticket_number}
                </div>
                <StatusBadge
                  status={detail.ticket.status}
                />
              </div>
              <button
  onClick={() => {
    setSelected(null);
    setDetail(null);
    setProof(null);
    setVisitNotes([]);
  }}
                style={{
                  background:'none',
                  border:'none',
                  color:'var(--text3)',
                  cursor:'pointer',
                  fontSize:'18px',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding:'20px' }}>
              {/* Ticket Info */}
              {[
                ['Type',
                  detail.ticket.ticket_type
                    ?.replace('_',' ')],
                ['Order',
                  detail.ticket.order_type],
                ['Priority',
                  detail.ticket.priority],
                ['Customer',
                  detail.ticket.customer_name],
                ['Engineer',
                  detail.ticket.engineer_name
                  || 'Unassigned'],
                ['Product',
                  detail.ticket.product_name
                  || detail.ticket
                    .product_description
                  || '—'],
                ['Serial',
                  detail.ticket.serial_number
                  || '—'],
                ['Qty',
                  detail.ticket.quantity],
                ['Scheduled',
                  detail.ticket.scheduled_date
                    ? new Date(
                        detail.ticket
                          .scheduled_date
                      ).toLocaleDateString(
                        'en-IN')
                    : '—'],
                ['Slot',
                  detail.ticket.scheduled_slot
                  || '—'],
                ['Contact',
                  detail.ticket.contact_person
                  || '—'],
                ['Contact Ph',
                  detail.ticket.contact_phone
                  || '—'],
              ].map(([label, value]) => (
                <div key={label} style={{
                  display:'flex',
                  justifyContent:'space-between',
                  padding:'7px 0',
                  borderBottom:
                    '1px solid var(--border)',
                  fontSize:'12.5px',
                }}>
                  <span style={{
                    color:'var(--text3)',
                    fontFamily:
                      'DM Mono,monospace',
                    fontSize:'11px',
                  }}>
                    {label}
                  </span>
                  <span style={{
                    color:'var(--text)',
                    fontWeight:'500',
                    textAlign:'right',
                    maxWidth:'200px',
                    textTransform:'capitalize',
                  }}>
                    {value}
                  </span>
                </div>
              ))}

              {/* Notes */}
              {detail.ticket
                .notes_for_engineer && (
                <div style={{
                  marginTop:'12px',
                  padding:'12px',
                  background:'var(--surface2)',
                  borderRadius:'8px',
                  fontSize:'12.5px',
                  color:'var(--text2)',
                }}>
                  <div style={{
                    fontSize:'10px',
                    fontFamily:
                      'DM Mono,monospace',
                    color:'var(--text3)',
                    marginBottom:'6px',
                    letterSpacing:'1px',
                  }}>
                    ENGINEER NOTES
                  </div>
                  {detail.ticket
                    .notes_for_engineer}
                </div>
              )}

              {/* Assign Engineer */}
              {!detail.ticket
                .assigned_engineer_id && (
                <div style={{
                  marginTop:'16px',
                  padding:'16px',
                  background:'var(--surface2)',
                  borderRadius:'8px',
                }}>
                  <div style={{
                    fontSize:'11px',
                    fontFamily:
                      'DM Mono,monospace',
                    color:'var(--text3)',
                    letterSpacing:'1px',
                    marginBottom:'10px',
                  }}>
                    ASSIGN ENGINEER
                  </div>
                  <select
                    value={assignEng}
                    onChange={e =>
                      setAssignEng(
                        e.target.value)}
                    style={{
                      width:'100%',
                      background:
                        'var(--surface)',
                      border:
                        '1px solid var(--border2)',
                      color:'var(--text)',
                      padding:'9px 12px',
                      borderRadius:'6px',
                      fontSize:'13px',
                      outline:'none',
                      marginBottom:'8px',
                    }}
                  >
                    <option value="">
                      — Select Engineer —
                    </option>
                    {engineers.map(e => (
                      <option key={e.id}
                        value={e.id}>
                        {e.name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() =>
                      handleAssign(
                        detail.ticket.id)}
                    disabled={assigning}
                    style={{
                      width:'100%',
                      background:
                        'var(--accent)',
                      color:'var(--bg)',
                      border:'none',
                      padding:'9px',
                      borderRadius:'6px',
                      fontSize:'13px',
                      fontWeight:'600',
                      cursor:'pointer',
                    }}
                  >
                    {assigning
                      ? 'Assigning...'
                      : 'Assign Engineer'}
                  </button>
                </div>
              )}

              {/* Assign Freelancer */}
              {!detail.ticket.assigned_engineer_id &&
                !detail.ticket.assigned_freelancer_id && (
                <div style={{
                  marginTop:'16px',
                  padding:'16px',
                  background:'var(--surface2)',
                  borderRadius:'8px',
                  border:'1px dashed var(--border2)',
                }}>
                  <div style={{
                    fontSize:'11px',
                    fontFamily:'DM Mono,monospace',
                    color:'var(--text3)',
                    letterSpacing:'1px',
                    marginBottom:'10px',
                  }}>
                    ASSIGN TO FREELANCER
                  </div>
                  {freelancers.length === 0 ? (
                    <div style={{
                      fontSize:'12px',
                      color:'var(--text3)',
                      marginBottom:'8px',
                    }}>
                      No freelancers in your directory yet.
                      Add one from the Freelancers page first.
                    </div>
                  ) : (
                    <>
                      <select
                        value={assignFreelancerId}
                        onChange={e =>
                          setAssignFreelancerId(e.target.value)}
                        style={{
                          width:'100%',
                          background:'var(--surface)',
                          border:'1px solid var(--border2)',
                          color:'var(--text)',
                          padding:'9px 12px',
                          borderRadius:'6px',
                          fontSize:'13px',
                          outline:'none',
                          marginBottom:'8px',
                        }}
                      >
                        <option value="">
                          — Select Freelancer —
                        </option>
                        {freelancers.map(f => (
                          <option key={f.id} value={f.id}>
                            {f.name}{f.city ? ` (${f.city})` : ''}
                          </option>
                        ))}
                      </select>
                      <textarea
                        value={freelancerInstructions}
                        onChange={e =>
                          setFreelancerInstructions(e.target.value)}
                        placeholder="Instructions for the freelancer (optional) — e.g. gate code, contact person"
                        style={{
                          width:'100%',
                          background:'var(--surface)',
                          border:'1px solid var(--border2)',
                          color:'var(--text)',
                          padding:'9px 12px',
                          borderRadius:'6px',
                          fontSize:'12.5px',
                          outline:'none',
                          marginBottom:'8px',
                          minHeight:'60px',
                          resize:'vertical',
                          fontFamily:'inherit',
                          boxSizing:'border-box',
                        }}
                      />
                      <button
                        onClick={() =>
                          handleAssignFreelancer(detail.ticket.id)}
                        disabled={assigningFreelancer}
                        style={{
                          width:'100%',
                          background:'var(--purple)',
                          color:'var(--bg)',
                          border:'none',
                          padding:'9px',
                          borderRadius:'6px',
                          fontSize:'13px',
                          fontWeight:'600',
                          cursor:'pointer',
                        }}
                      >
                        {assigningFreelancer
                          ? 'Generating Link...'
                          : 'Assign & Generate Link'}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Freelancer Assigned Info */}
              {detail.ticket.assigned_freelancer_id && (
                <div style={{
                  marginTop:'16px',
                  padding:'16px',
                  background:'var(--surface2)',
                  borderRadius:'8px',
                }}>
                  <div style={{
                    fontSize:'11px',
                    fontFamily:'DM Mono,monospace',
                    color:'var(--text3)',
                    letterSpacing:'1px',
                    marginBottom:'10px',
                  }}>
                    ASSIGNED FREELANCER
                  </div>
                  <div style={{
                    fontSize:'14px',
                    fontWeight:'600',
                    marginBottom:'4px',
                  }}>
                    {detail.ticket.freelancer_name}
                  </div>
                  {detail.ticket.freelancer_phone && (
                    <div style={{
                      fontSize:'12px',
                      color:'var(--text3)',
                      fontFamily:'DM Mono,monospace',
                      marginBottom:'12px',
                    }}>
                      {detail.ticket.freelancer_phone}
                    </div>
                  )}
                  {detail.ticket.vendor_instructions && (
                    <div style={{
                      fontSize:'12px',
                      color:'var(--text2)',
                      background:'var(--surface)',
                      padding:'10px',
                      borderRadius:'6px',
                      marginBottom:'12px',
                    }}>
                      {detail.ticket.vendor_instructions}
                    </div>
                  )}
                  <button
                    onClick={() =>
                      handleRegenerateFreelancerLink(detail.ticket.id)}
                    disabled={assigningFreelancer}
                    style={{
                      width:'100%',
                      background:'transparent',
                      color:'var(--purple)',
                      border:'1px solid var(--purple)',
                      padding:'9px',
                      borderRadius:'6px',
                      fontSize:'12.5px',
                      fontWeight:'600',
                      cursor:'pointer',
                    }}
                  >
                    {assigningFreelancer
                      ? 'Generating...'
                      : 'Generate New Link'}
                  </button>
                </div>
              )}

              {/* Generated Link Display */}
              {freelancerLink && (
                <div style={{
                  marginTop:'12px',
                  padding:'14px',
                  background:'rgba(159,122,234,0.1)',
                  border:'1px solid rgba(159,122,234,0.3)',
                  borderRadius:'8px',
                }}>
                  <div style={{
                    fontSize:'11px',
                    fontFamily:'DM Mono,monospace',
                    color:'var(--purple)',
                    letterSpacing:'1px',
                    marginBottom:'8px',
                  }}>
                    TASK LINK — SHARE VIA WHATSAPP OR EMAIL
                  </div>
                  <div style={{
                    fontSize:'11.5px',
                    color:'var(--text2)',
                    wordBreak:'break-all',
                    background:'var(--surface)',
                    padding:'8px 10px',
                    borderRadius:'6px',
                    marginBottom:'8px',
                    fontFamily:'DM Mono,monospace',
                  }}>
                    {freelancerLink}
                  </div>
                  <button
                    onClick={handleCopyLink}
                    style={{
                      width:'100%',
                      background: linkCopied
                        ? 'var(--green)' : 'var(--purple)',
                      color:'var(--bg)',
                      border:'none',
                      padding:'9px',
                      borderRadius:'6px',
                      fontSize:'12.5px',
                      fontWeight:'600',
                      cursor:'pointer',
                    }}
                  >
                    {linkCopied ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
              )}

              {/* Status Actions */}
              <div style={{
                marginTop:'16px',
                display:'flex',
                flexDirection:'column',
                gap:'8px',
              }}>
                <div style={{
                  fontSize:'11px',
                  fontFamily:
                    'DM Mono,monospace',
                  color:'var(--text3)',
                  letterSpacing:'1px',
                  marginBottom:'4px',
                }}>
                  UPDATE STATUS
                </div>
                {detail.ticket.status ===
                  'assigned' && (
                  <button
                    onClick={() =>
                      handleStatusUpdate(
                        detail.ticket.id,
                        'in_progress')}
                    style={{
                      background:
                        'rgba(255,192,64,0.1)',
                      color:'var(--yellow)',
                      border:
                        '1px solid rgba(255,192,64,0.3)',
                      padding:'9px',
                      borderRadius:'8px',
                      fontSize:'13px',
                      fontWeight:'600',
                      cursor:'pointer',
                    }}
                  >
                    Mark In Progress
                  </button>
                )}
                {['in_progress','assigned']
                  .includes(
                    detail.ticket.status) && (
                  <button
                    onClick={() =>
                      handleStatusUpdate(
                        detail.ticket.id,
                        'delivered')}
                    style={{
                      background:
                        'rgba(0,229,160,0.1)',
                      color:'var(--green)',
                      border:
                        '1px solid rgba(0,229,160,0.3)',
                      padding:'9px',
                      borderRadius:'8px',
                      fontSize:'13px',
                      fontWeight:'600',
                      cursor:'pointer',
                    }}
                  >
                    Mark Delivered
                  </button>
                )}
                {detail.ticket.status ===
                  'delivered' && (
                  <button
                    onClick={() =>
                      handleClose(
                        detail.ticket.id)}
                    style={{
                      background:
                        'rgba(0,229,160,0.15)',
                      color:'var(--green)',
                      border:
                        '1px solid rgba(0,229,160,0.4)',
                      padding:'9px',
                      borderRadius:'8px',
                      fontSize:'13px',
                      fontWeight:'700',
                      cursor:'pointer',
                    }}
                  >
                    ✓ Close Ticket
                  </button>
                )}
                {!['closed','cancelled',
                   'delivered']
                  .includes(
                    detail.ticket.status) && (
                  <button
                    onClick={() =>
                      handleStatusUpdate(
                        detail.ticket.id,
                        'cancelled')}
                    style={{
                      background:
                        'rgba(255,77,109,0.1)',
                      color:'var(--red)',
                      border:
                        '1px solid rgba(255,77,109,0.3)',
                      padding:'9px',
                      borderRadius:'8px',
                      fontSize:'13px',
                      fontWeight:'600',
                      cursor:'pointer',
                    }}
                  >
                    Cancel Ticket
                  </button>
                )}
              </div>

              {/* Timeline */}
              {detail.timeline?.length > 0 && (
                <div style={{
                  marginTop:'20px',
                }}>
                  <div style={{
                    fontSize:'11px',
                    fontFamily:
                      'DM Mono,monospace',
                    color:'var(--text3)',
                    letterSpacing:'1px',
                    marginBottom:'12px',
                  }}>
                    TIMELINE
                  </div>
                  {detail.timeline.map(
                    (e, i) => (
                    <div key={e.id} style={{
                      display:'flex',
                      gap:'12px',
                      paddingBottom:'14px',
                      position:'relative',
                    }}>
                      {i < detail.timeline
                        .length - 1 && (
                        <div style={{
                          position:'absolute',
                          left:'10px', top:'22px',
                          bottom:0, width:'1px',
                          background:
                            'var(--border)',
                        }} />
                      )}
                      <div style={{
                        width:'20px',
                        height:'20px',
                        borderRadius:'50%',
                        background:
                          e.event_type ===
                          'delivered'
                            ? 'var(--green)'
                          : e.event_type ===
                            'created'
                            ? 'var(--accent)'
                          : e.event_type ===
                            'closed'
                            ? 'var(--green)'
                          : 'var(--surface3)',
                        flexShrink:0,
                        border:
                          '2px solid var(--border)',
                        zIndex:1,
                      }} />
                      <div style={{ flex:1 }}>
                        <div style={{
                          fontSize:'12.5px',
                          fontWeight:'500',
                        }}>
                          {e.description}
                        </div>
                        <div style={{
                          fontSize:'10px',
                          color:'var(--text3)',
                          fontFamily:
                            'DM Mono,monospace',
                          marginTop:'2px',
                        }}>
                          {e.performed_by_name}
                          {' · '}
                          {new Date(e.created_at)
                            .toLocaleString(
                              'en-IN',
                              { day:'2-digit',
                                month:'short',
                                hour:'2-digit',
                                minute:'2-digit'
                              })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Proof of Work */}
              {proof && (
                <div style={{ marginTop:'20px' }}>
                  <div style={{
                    fontSize:'11px',
                    fontFamily:'DM Mono,monospace',
                    color:'var(--text3)',
                    letterSpacing:'1px',
                    marginBottom:'12px',
                  }}>
                    PROOF OF WORK
                  </div>

                  {proof.images?.length > 0 && (
                    <div style={{
                      display:'grid',
                      gridTemplateColumns:
                        'repeat(auto-fill, minmax(90px, 1fr))',
                      gap:'8px',
                      marginBottom:'16px',
                    }}>
                      {proof.images.map(img => (
                        <a key={img.id}
                          href={img.image_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={img.image_url}
                            alt={img.image_type}
                            style={{
                              width:'100%',
                              height:'90px',
                              objectFit:'cover',
                              borderRadius:'8px',
                              border:'1px solid var(--border)',
                            }}
                          />
                        </a>
                      ))}
                    </div>
                  )}

                  {proof.engineer_notes && (
                    <div style={{
                      background:'var(--surface2)',
                      border:'1px solid var(--border)',
                      borderRadius:'8px',
                      padding:'12px 14px',
                      marginBottom:'16px',
                      fontSize:'13px',
                      color:'var(--text)',
                      lineHeight:'1.4',
                    }}>
                      <div style={{
                        fontSize:'10px',
                        fontFamily:'DM Mono,monospace',
                        color:'var(--text3)',
                        letterSpacing:'1px',
                        marginBottom:'6px',
                      }}>
                        ENGINEER NOTES
                      </div>
                      {proof.engineer_notes}
                    </div>
                  )}

                  {proof.customer_signature_url && (
                    <div>
                      <div style={{
                        fontSize:'10px',
                        fontFamily:'DM Mono,monospace',
                        color:'var(--text3)',
                        marginBottom:'6px',
                        letterSpacing:'1px',
                      }}>
                        CUSTOMER SIGNATURE
                        {proof.customer_name
                          ? ` — ${proof.customer_name}`
                          : ''}
                      </div>
                      <img
                        src={proof.customer_signature_url}
                        alt="Customer signature"
                        style={{
                          width:'160px',
                          background:'#fff',
                          borderRadius:'8px',
                          border:'1px solid var(--border)',
                          padding:'8px',
                        }}
                      />
                    </div>
                  )}

                  {proof.completion_latitude && (
                    <div style={{
                      marginTop:'12px',
                      fontSize:'11px',
                      fontFamily:'DM Mono,monospace',
                    }}>
                      <span style={{
                        color:'var(--text3)',
                        letterSpacing:'1px',
                        marginRight:'8px',
                      }}>
                        COMPLETION LOCATION
                      </span>
                      <a
                        href={`https://www.google.com/maps?q=${proof.completion_latitude},${proof.completion_longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color:'var(--accent)',
                          textDecoration:'underline',
                        }}
                      >
                        📍 View location ↗
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Visit Notes (incomplete job attempts) */}
              {visitNotes.length > 0 && (
                <div style={{ marginTop:'20px' }}>
                  <div style={{
                    fontSize:'11px',
                    fontFamily:'DM Mono,monospace',
                    color:'var(--text3)',
                    letterSpacing:'1px',
                    marginBottom:'12px',
                  }}>
                    VISIT NOTES{' '}
                    ({visitNotes.length})
                  </div>
                  {visitNotes.map(n => (
                    <div key={n.id} style={{
                      background:'var(--surface2)',
                      border:'1px solid var(--border)',
                      borderLeft:
                        '3px solid var(--yellow)',
                      borderRadius:'8px',
                      padding:'12px 14px',
                      marginBottom:'10px',
                    }}>
                      <div style={{
                        fontSize:'13px',
                        color:'var(--text)',
                        marginBottom:'8px',
                        lineHeight:'1.4',
                      }}>
                        {n.note}
                      </div>

                      {n.photo_url && (
                        <a
                          href={n.photo_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={n.photo_url}
                            alt="Visit photo"
                            style={{
                              width:'90px',
                              height:'90px',
                              objectFit:'cover',
                              borderRadius:'8px',
                              border:'1px solid var(--border)',
                              marginBottom:'8px',
                              display:'block',
                            }}
                          />
                        </a>
                      )}

                      <div style={{
                        fontSize:'10px',
                        color:'var(--text2)',
                        fontFamily:'DM Mono,monospace',
                        display:'flex',
                        justifyContent:'space-between',
                        alignItems:'center',
                      }}>
                        <span>
                          {n.engineer_name}
                          {' · '}
                          {new Date(n.created_at)
                            .toLocaleString(
                              'en-IN',
                              { day:'2-digit',
                                month:'short',
                                hour:'2-digit',
                                minute:'2-digit'
                              })}
                        </span>
                        {n.latitude && (
                          <a
                            href={`https://www.google.com/maps?q=${n.latitude},${n.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color:'var(--accent)',
                              textDecoration:'underline',
                            }}
                          >
                            📍 View location ↗
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tickets;